import {
  ConfigDiff,
  ConfigLayerResponse,
  ConfigLayerUpdateResponse,
  ConfigVersionDetail,
  ConfigVersionSummary,
  RebuildSummary,
  ServerConfigStatus,
  type ConfigScopeRef,
  type SettingsPatch,
} from '@bartizo/shared';
import { z } from 'zod';
import { apiRequest } from './client.js';

/**
 * Settings (knowledge doc section 11).
 *
 * Every call here carries a `SettingsPatch` — a structured object the API validates
 * field by field. There is no call that takes settings as text, which is section
 * 11's "structured fields only" expressed where it cannot be worked around.
 */

/** `/config/layers/global`, `/config/layers/group/<id>`, `/config/layers/server/<id>`. */
const pathFor = (ref: ConfigScopeRef): string =>
  ref.scope === 'global'
    ? '/config/layers/global'
    : `/config/layers/${ref.scope}/${encodeURIComponent(ref.id)}`;

export const getConfigLayer = (ref: ConfigScopeRef): Promise<ConfigLayerResponse> =>
  apiRequest(pathFor(ref), { schema: ConfigLayerResponse });

export const putConfigLayer = (
  ref: ConfigScopeRef,
  settings: SettingsPatch,
): Promise<ConfigLayerUpdateResponse> =>
  apiRequest(pathFor(ref), {
    method: 'PUT',
    body: { settings },
    schema: ConfigLayerUpdateResponse,
  });

export const getConfigRollout = (): Promise<ServerConfigStatus[]> =>
  apiRequest('/config/status', { schema: z.array(ServerConfigStatus) });

export const listConfigVersions = (serverId: string): Promise<ConfigVersionSummary[]> =>
  apiRequest(`/config/versions/${encodeURIComponent(serverId)}`, {
    schema: z.array(ConfigVersionSummary),
  });

export const getConfigVersion = (serverId: string, version: number): Promise<ConfigVersionDetail> =>
  apiRequest(`/config/versions/${encodeURIComponent(serverId)}/${String(version)}`, {
    schema: ConfigVersionDetail,
  });

export const getConfigDiff = (serverId: string, from: number, to: number): Promise<ConfigDiff> =>
  apiRequest(
    `/config/diff?server_id=${encodeURIComponent(serverId)}&from=${String(from)}&to=${String(to)}`,
    { schema: ConfigDiff },
  );

export const revertConfig = (
  serverId: string,
  version: number,
): Promise<{ rebuild: RebuildSummary }> =>
  apiRequest('/config/revert', {
    method: 'POST',
    body: { serverId, version },
    schema: z.object({ rebuild: RebuildSummary }),
  });
